import { FC, ReactNode, useEffect, useRef, useState } from 'react'
import cn from './index.module.css'
import FieldWrapper from '../FieldWrapper/FieldWrapper'
import { Select } from 'antd'
import classNames from 'classnames'
import { filterOptionsByLabel } from '@/helpers/new/filterOptionsByLabel'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import fitContentClasses from '@pages/Admin/CRM/components/base/Select/selectDropdownFitContent.module.css'
import {
  enhanceSelectOptionsWithColors,
  getSelectOptionSearchText,
} from '@pages/Admin/CRM/components/base/Select/utils/selectOptionColor'

interface ISelectField {
  label?: ReactNode
  tooltip?: ReactNode
  value?: string[] | string | null
  defaultValue?: string[] | string | null
  mode?: 'multiple' | 'tags'
  placeholder?: string
  notFoundContent?: ReactNode
  isInvalid?: boolean
  options?: Array<{
    label: ReactNode
    value: string | null
    title?: string
    color?: string | null
    disabled?: boolean
  }>
  className?: string
  wrapperClassName?: string
  popupClassName?: string
  disabled?: boolean
  loading?: boolean
  allowClear?: boolean | undefined
  openOnClick?: boolean
  showSearch?: boolean
  size?: 'normal' | 'small' | 'xs' | 'compact'
  /** В multiple/tags: вместо тегов показывать только число выбранных */
  showSelectionCount?: boolean
  formatSelectionCount?: (count: number) => ReactNode
  /** false — ширина как у селекта; по умолчанию true для size=compact */
  dropdownFitContentWidth?: boolean
  showOptionColors?: boolean
  optionLabelProp?: string
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement
  description?: string | null
  required?: boolean
  onChange: (value: string[] | string | null) => void
  onSearch?: (value: string) => void
  onBlur?: () => void
  dropdownOpen?: boolean
  onDropdownVisibleChange?: (open: boolean) => void
}

const SelectField: FC<ISelectField> = ({
  label,
  tooltip,
  value,
  mode = undefined,
  defaultValue = null,
  placeholder,
  notFoundContent,
  options,
  isInvalid,
  className,
  wrapperClassName,
  popupClassName,
  disabled,
  loading,
  allowClear,
  openOnClick,
  showSearch = true,
  size,
  showSelectionCount = false,
  formatSelectionCount,
  dropdownFitContentWidth,
  showOptionColors = false,
  optionLabelProp,
  getPopupContainer,
  description,
  required,
  onChange,
  onSearch,
  onBlur,
  dropdownOpen,
  onDropdownVisibleChange,
}) => {
  const [clickOpen, setClickOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement | null>(null)

  const openSelect = (event: React.MouseEvent | React.TouchEvent) => {
    if (!openOnClick || disabled) return
    event.preventDefault()
    event.stopPropagation()
    setClickOpen((prev) => !prev)
  }

  useEffect(() => {
    if (!openOnClick || !clickOpen) return

    const closeSelect = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null
      if (target && wrapperRef.current?.contains(target)) return
      if (
        target instanceof Element &&
        (target.closest('.ant-select-dropdown') ||
          target.closest('[class*="ant-select-item"]'))
      ) {
        return
      }

      setClickOpen(false)
    }

    document.addEventListener('click', closeSelect, true)

    return () => {
      document.removeEventListener('click', closeSelect, true)
    }
  }, [clickOpen, openOnClick])

  const handleDropdownVisibleChange = (nextOpen: boolean) => {
    if (openOnClick) {
      setClickOpen(nextOpen)
    }
    onDropdownVisibleChange?.(nextOpen)
  }

  const isMultipleMode = mode === 'multiple' || mode === 'tags'
  const useSelectionCount = showSelectionCount && isMultipleMode
  const fitDropdownToContent = dropdownFitContentWidth ?? size === 'compact'
  const resolvedOptions = enhanceSelectOptionsWithColors(
    options,
    showOptionColors,
  )

  const renderSelectionCount = (count: number) => {
    if (formatSelectionCount) {
      return formatSelectionCount(count)
    }

    return count
  }

  return (
    <FieldWrapper
      label={label}
      tooltip={tooltip}
      description={description}
      isInvalid={isInvalid}
      required={required}
      className={wrapperClassName}
    >
      <div
        ref={wrapperRef}
        className={cn.selectClickArea}
        onMouseDownCapture={openSelect}
        onTouchStartCapture={openSelect}
      >
        <Select
          className={classNames(
            isMultipleMode ? cn.selectMultiple : cn.select,
            isInvalid && cn.invalid,
            size === 'small' && cn.smallSize,
            size === 'small' && iclasses.newSelectSmall,
            size === 'xs' && cn.xsSize,
            size === 'xs' && iclasses.newSelectXs,
            size === 'compact' && cn.compactSize,
            size === 'compact' && iclasses.newSelectCompact,
            size === 'compact' && isMultipleMode && iclasses.newSelectMultiple,
            size === 'compact' && !isMultipleMode && iclasses.newSelect,
            useSelectionCount && cn.selectionCountOnly,
            showOptionColors && cn.selectWithOptionColors,
            loading && !disabled && iclasses.newSelectLoadingLocked,
            className,
          )}
          maxTagCount={useSelectionCount ? 0 : undefined}
          maxTagPlaceholder={
            useSelectionCount
              ? (omittedValues) => (
                  <span className={cn.selectionCountLabel}>
                    <span className={cn.selectionCountText}>выбрано</span>
                    <span className={cn.selectionCount}>
                      {renderSelectionCount(omittedValues.length)}
                    </span>
                  </span>
                )
              : undefined
          }
          loading={loading}
          disabled={disabled}
          defaultValue={defaultValue}
          value={value ?? null}
          placeholder={placeholder}
          onChange={(e) => {
            if (openOnClick) return
            onChange(e === undefined ? null : e)
          }}
          onSelect={(selectedValue: string | number) => {
            if (!openOnClick) return
            setClickOpen(false)
            onChange(selectedValue === undefined ? null : String(selectedValue))
          }}
          open={openOnClick ? clickOpen : dropdownOpen}
          onDropdownVisibleChange={handleDropdownVisibleChange}
          getPopupContainer={getPopupContainer}
          //@ts-ignore
          options={resolvedOptions}
          optionLabelProp={showOptionColors ? undefined : optionLabelProp}
          mode={mode}
          showSearch={showSearch}
          allowClear={allowClear}
          notFoundContent={notFoundContent}
          filterOption={
            showOptionColors ||
            resolvedOptions.some((option) => typeof option?.label !== 'string')
              ? (input, option) =>
                  getSelectOptionSearchText(option)
                    .toLowerCase()
                    .includes(input.toLowerCase())
              : filterOptionsByLabel
          }
          popupClassName={classNames(
            cn.selectPopup,
            size === 'compact' && cn.selectPopupCompact,
            fitDropdownToContent && fitContentClasses.dropdownFitContent,
            popupClassName,
          )}
          dropdownMatchSelectWidth={!fitDropdownToContent}
          onSearch={onSearch}
          onBlur={onBlur}
          // filterSort={sort}
        />
      </div>
    </FieldWrapper>
  )
}

export default SelectField
